import React from 'react';
import { GraduationCap, BookOpen, Calendar, MapPin } from 'lucide-react';
import { education } from '../data/mock';

const Education = () => {
  return (
    <section id="education" className="py-20 bg-[rgb(26,28,30)]">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Education <span className="text-[rgb(218,255,1)]">&</span> Learning
            </h2>
            <p className="text-xl text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
              Academic foundation and continuous learning journey in computer engineering and AI/ML
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Main Education Card */}
            <div className="group bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 hover:border-[rgb(218,255,1)] hover:transform hover:translate-y-[-4px] transition-all duration-300 relative overflow-hidden">
              {/* Top accent border */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-[rgb(218,255,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="flex items-start space-x-6">
                <div className="w-16 h-16 rounded-2xl bg-[rgb(218,255,1)]/10 flex items-center justify-center text-[rgb(218,255,1)] group-hover:bg-[rgb(218,255,1)]/20 transition-colors duration-300">
                  <GraduationCap size={32} />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[rgb(218,255,1)] transition-colors duration-300">
                    {education.degree}
                  </h3>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex items-center text-[rgb(218,218,218)]">
                      <MapPin size={16} className="mr-2 text-[rgb(161,161,170)]" />
                      {education.institution}
                    </div>
                    
                    <div className="flex items-center text-[rgb(161,161,170)]">
                      <Calendar size={16} className="mr-2" />
                      {education.duration}
                    </div>
                  </div>
                  
                  <p className="text-[rgb(161,161,170)] leading-relaxed">
                    Comprehensive program covering fundamental and advanced topics in computer engineering, 
                    with special focus on machine learning, data structures, and software engineering principles.
                  </p>
                </div>
              </div>
            </div>

            {/* Coursework */}
            <div className="space-y-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[rgb(218,255,1)]/10 flex items-center justify-center">
                  <BookOpen size={20} className="text-[rgb(218,255,1)]" />
                </div>
                <h3 className="text-xl font-semibold text-white">Relevant Coursework</h3>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {education.coursework.map((course, index) => (
                  <div
                    key={index}
                    className="group bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-xl p-4 hover:border-[rgb(218,255,1)] hover:bg-[rgb(218,255,1)]/5 transition-all duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-[rgb(218,255,1)] opacity-60 group-hover:opacity-100 transition-opacity duration-200"></div>
                      <span className="text-[rgb(218,218,218)] font-medium text-sm group-hover:text-[rgb(218,255,1)] transition-colors duration-200">
                        {course}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Academic Achievements */}
          <div className="mt-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[rgb(218,255,1)]/10 flex items-center justify-center">
                  <div className="text-2xl font-bold text-[rgb(218,255,1)]">4.5</div>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Years</h4>
                <p className="text-sm text-[rgb(161,161,170)]">Engineering Program</p>
              </div>
              
              <div className="text-center bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[rgb(218,255,1)]/10 flex items-center justify-center">
                  <div className="text-2xl font-bold text-[rgb(218,255,1)]">8+</div>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Core Subjects</h4>
                <p className="text-sm text-[rgb(161,161,170)]">Computer Engineering</p>
              </div>
              
              <div className="text-center bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[rgb(218,255,1)]/10 flex items-center justify-center">
                  <div className="text-2xl font-bold text-[rgb(218,255,1)]">2024</div>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Graduated</h4>
                <p className="text-sm text-[rgb(161,161,170)]">April 2024</p>
              </div>
            </div>
          </div>

          {/* Learning Philosophy */}
          <div className="mt-16 bg-[rgb(17,17,19)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
            <div className="text-center">
              <h3 className="text-2xl font-semibold text-white mb-4">
                Continuous <span className="text-[rgb(218,255,1)]">Learning</span>
              </h3>
              <p className="text-[rgb(161,161,170)] max-w-3xl mx-auto leading-relaxed">
                Beyond formal education, I believe in lifelong learning. I regularly engage with online courses, 
                research papers, and hands-on projects to stay current with rapidly evolving AI/ML technologies. 
                My academic foundation provides the theoretical understanding, while practical projects and 
                certifications keep me at the forefront of industry trends.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8">
              <div className="text-center">
                <div className="text-lg font-bold text-[rgb(218,255,1)] mb-1">Theoretical</div>
                <div className="text-sm text-[rgb(161,161,170)]">Strong Foundation</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-[rgb(218,255,1)] mb-1">Practical</div>
                <div className="text-sm text-[rgb(161,161,170)]">Project-Based</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-[rgb(218,255,1)] mb-1">Current</div>
                <div className="text-sm text-[rgb(161,161,170)]">Industry Relevant</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-[rgb(218,255,1)] mb-1">Continuous</div>
                <div className="text-sm text-[rgb(161,161,170)]">Always Learning</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Education;